/**
 * Audio timestamp utilities (PoC frontend/src/utils/audioTimestamp.ts より移植)
 *
 * HH:MM:SS文字列 ↔ 秒数の変換、発話インデックス構築、二分探索
 */

import type { TimelineBlock } from '@/types/review';

/** フラット化された発話エントリ（二分探索用） */
export interface IndexedUtterance {
  id: string;
  startSeconds: number;
  /** 次の発話の開始時刻。最終発話は Infinity */
  endSeconds: number;
}

/**
 * "HH:MM:SS" or "MM:SS" → 秒数
 */
export function parseTimestamp(ts: string): number {
  const parts = ts.split(':').map(Number);
  if (parts.length === 3) return (parts[0] ?? 0) * 3600 + (parts[1] ?? 0) * 60 + (parts[2] ?? 0);
  if (parts.length === 2) return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
  return 0;
}

/**
 * 秒数 → "HH:MM:SS"
 */
export function formatSeconds(totalSec: number): string {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.floor(totalSec % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * ブロック配列から全発話のフラットソート済みインデックスを構築。
 * endSeconds は次の発話の startSeconds から推定。
 */
export function buildUtteranceIndex(blocks: TimelineBlock[]): IndexedUtterance[] {
  const flat: { id: string; startSeconds: number }[] = [];
  for (const block of blocks) {
    for (const utt of block.utterances) {
      flat.push({ id: utt.id, startSeconds: parseTimestamp(utt.timestamp) });
    }
  }
  flat.sort((a, b) => a.startSeconds - b.startSeconds);

  return flat.map((entry, i) => ({
    id: entry.id,
    startSeconds: entry.startSeconds,
    endSeconds: i + 1 < flat.length ? (flat[i + 1]?.startSeconds ?? Infinity) : Infinity,
  }));
}

/**
 * 二分探索で currentSeconds に対応する発話を返す。
 */
export function findUtteranceAtTime(
  index: IndexedUtterance[],
  currentSeconds: number,
): IndexedUtterance | null {
  if (index.length === 0) return null;
  const first = index[0];
  if (!first || currentSeconds < first.startSeconds) return null;

  let lo = 0;
  let hi = index.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    const midEntry = index[mid];
    if (midEntry && midEntry.startSeconds <= currentSeconds) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  const entry = index[lo];
  if (entry && currentSeconds >= entry.startSeconds && currentSeconds < entry.endSeconds) {
    return entry;
  }
  return null;
}
